import axios from 'axios';

const API_URL = process.env.VUE_APP_API_URL || 'https://conrad451.pythonanywhere.com';

const apiClient = axios.create({
  baseURL: API_URL,
});

// Request interceptor: auto-attach Bearer token
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor: auto-refresh on 401
let isRefreshing = false;
let failedQueue = [];

function processQueue(error, token = null) {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config;

    // Don't retry auth endpoints or already-retried requests
    if (
      !error.response ||
      error.response.status !== 401 ||
      originalRequest._retry ||
      originalRequest.url.includes('/auth/login/') ||
      originalRequest.url.includes('/auth/refresh/')
    ) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return apiClient(originalRequest);
      });
    }

    originalRequest._retry = true;
    isRefreshing = true;

    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    // Use plain axios (not apiClient) to avoid interceptor loop
    return new Promise((resolve, reject) => {
      axios
        .post(`${API_URL}/auth/refresh/`, { refresh: refreshToken })
        .then(({ data }) => {
          const newToken = data.access;
          localStorage.setItem('accessToken', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue(null, newToken);
          resolve(apiClient(originalRequest));
        })
        .catch((err) => {
          processQueue(err, null);
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = '/login';
          reject(err);
        })
        .finally(() => {
          isRefreshing = false;
        });
    });
  }
);

export class APIService {
  // ---------- Helpers ----------
  _authHeaders(accessToken) {
    return {
      Authorization: `Bearer ${accessToken}`,
    };
  }

  // ---------- Auth endpoints ----------

  signup(payload) {
    // POST /auth/signup/  => { id, username, email, role, ... }
    return apiClient.post('/auth/signup/', payload);
  }

  login(payload) {
    // POST /auth/login/   => { refresh, access }
    return apiClient.post('/auth/login/', payload);
  }

  refresh(refreshToken) {
    // POST /auth/refresh/ => { access }
    return apiClient.post('/auth/refresh/', { refresh: refreshToken });
  }

  account(accessToken) {
    // GET /auth/account/  => { id, username, email, role, ... }
    return apiClient.get('/auth/account/', {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Orders endpoints ----------

  getOrders(accessToken) {
    // GET /orders/
    return apiClient.get('/orders/', {
      headers: this._authHeaders(accessToken),
    });
  }

  getOrder(pk, accessToken) {
    // GET /orders/{pk}/
    return apiClient.get(`/orders/${pk}/`, {
      headers: this._authHeaders(accessToken),
    });
  }

  createOrder(payload, accessToken) {
    // POST /orders/create/
    return apiClient.post('/orders/create/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  bulkUpdateStatus(payload, accessToken) {
    return apiClient.post('/orders/bulk-status/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  completeOrder(pk, accessToken) {
    // POST /orders/{pk}/complete/
    return apiClient.post(`/orders/${pk}/complete/`, {}, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateOrderStatus(orderId, payload, accessToken) {
    return apiClient.put(`/orders/${orderId}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  listAllIssues(accessToken) {
    // GET /issues/
    return apiClient.get('/issues/', {
      headers: this._authHeaders(accessToken),
    });
  }

  updateOrderStep(orderId, orderStepId, payload, accessToken) {
    // PATCH /orders/{order_id}/steps/{pk}/
    // payload: { step_status, notes }
    return apiClient.patch(`/orders/${orderId}/steps/${orderStepId}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Issues (per order) ----------

  createOrderIssue(orderId, payload, accessToken) {
    // POST /orders/{order_id}/issues/
    return apiClient.post(`/orders/${orderId}/issues/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateOrderIssue(orderId, issueId, payload, accessToken) {
    // PATCH /orders/{order_id}/issues/{pk}/
    return apiClient.patch(`/orders/${orderId}/issues/${issueId}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Template step ----------

  getSteps(accessToken) {
    // GET /steps/
    return apiClient.get('/steps/', {
      headers: this._authHeaders(accessToken),
    });
  }

  createStep(payload, accessToken) {
    // POST /steps/
    return apiClient.post('/steps/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateStep(pk, payload, accessToken) {
    // PATCH /steps/{pk}/
    return apiClient.patch(`/steps/${pk}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Templates ----------

  getTemplates(accessToken) {
    // GET /templates/
    return apiClient.get('/templates/', {
      headers: this._authHeaders(accessToken),
    });
  }

  getTemplate(pk, accessToken) {
    // GET /templates/{pk}/
    return apiClient.get(`/templates/${pk}/`, {
      headers: this._authHeaders(accessToken),
    });
  }

  createTemplate(payload, accessToken) {
    // POST /templates/
    return apiClient.post('/templates/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateTemplate(pk, payload, accessToken) {
    // PATCH /templates/{pk}/
    return apiClient.patch(`/templates/${pk}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Customers ----------

  getCustomers(accessToken) {
    return apiClient.get('/customers/', {
      headers: this._authHeaders(accessToken),
    });
  }

  getCustomer(pk, accessToken) {
    return apiClient.get(`/customers/${pk}/`, {
      headers: this._authHeaders(accessToken),
    });
  }

  createCustomer(payload, accessToken) {
    return apiClient.post('/customers/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateCustomer(pk, payload, accessToken) {
    return apiClient.patch(`/customers/${pk}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Reporting ----------

  getOrderReporting(accessToken) {
    // GET /reporting/orders/
    return apiClient.get('/reporting/orders/', {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Dashboard & Enhanced Reporting ----------

  getDashboard(accessToken) {
    return apiClient.get('/reporting/dashboard/', {
      headers: this._authHeaders(accessToken),
    });
  }

  getEnhancedReporting(accessToken, params = {}) {
    return apiClient.get('/reporting/enhanced/', {
      headers: this._authHeaders(accessToken),
      params,
    });
  }

  getActivityLog(accessToken, params = {}) {
    return apiClient.get('/reporting/activity-log/', {
      headers: this._authHeaders(accessToken),
      params,
    });
  }

  getOrderActivity(orderId, accessToken) {
    return apiClient.get(`/reporting/orders/${orderId}/activity/`, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- Device Models ----------

  getDeviceModels(accessToken) {
    return apiClient.get('/device-models/', {
      headers: this._authHeaders(accessToken),
    });
  }

  getDeviceModel(pk, accessToken) {
    return apiClient.get(`/device-models/${pk}/`, {
      headers: this._authHeaders(accessToken),
    });
  }

  createDeviceModel(payload, accessToken) {
    return apiClient.post('/device-models/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  updateDeviceModel(pk, payload, accessToken) {
    return apiClient.patch(`/device-models/${pk}/`, payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- eBay ----------

  importEbayOrder(payload, accessToken) {
    return apiClient.post('/ebay/import/', payload, {
      headers: this._authHeaders(accessToken),
    });
  }

  linkEbayListing(orderId, ebayItemId, accessToken) {
    return apiClient.post('/ebay/link-listing/', {
      order_id: orderId,
      ebay_item_id: ebayItemId,
    }, {
      headers: this._authHeaders(accessToken),
    });
  }

  // ---------- CSV Import ----------

  importCSV(file, accessToken) {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post('/orders/import/', formData, {
      headers: {
        ...this._authHeaders(accessToken),
        'Content-Type': 'multipart/form-data',
      },
    });
  }
}
